import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_schedule_chat_message,
  API_list_scheduled_chat_messages,
  API_update_scheduled_chat_message,
  API_cancel_scheduled_chat_message,
  API_send_scheduled_chat_message_now,
} from "../../../constants";
import { countScheduledByChannel, isScheduledStreamMessageId } from "./scheduledMessageUtils";

// The signed-in user's scheduled messages (pending and failed) for the open
// company, and the actions on them. ChatDisplay owns the state and provides
// it, so the composer's send button, the channel banner, the sidebar row and
// the Scheduled panel all read one list.
export const ScheduledMessagesContext = createContext(null);

export const useScheduledMessages = () => useContext(ScheduledMessagesContext);

const payloadOf = (response) => response?.data?.redux?.payload || {};

export const scheduledMessageErrorText = (error, fallback) =>
  error?.response?.data?.system?.message || fallback;

const RECHECK_MS = 60 * 1000;

export function useScheduledMessagesState({ storeId, client, onOpenList, listOpen = false }) {
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const userId = client?.userID;
  // Only the latest list request may write the list: switching stores (and
  // so maybe companies) mid-request must not let the old store's answer land.
  const latestRequest = useRef(0);
  // A change applied here is newer than any list request already in flight,
  // whose snapshot may predate it: retire those so they can't undo it.
  const retireInFlightRefreshes = () => {
    latestRequest.current += 1;
  };

  const refresh = useCallback(async () => {
    const requestId = ++latestRequest.current;
    if (!storeId || !userId) {
      setScheduledMessages([]);
      return;
    }
    try {
      const response = await getRightworkAPI(API_list_scheduled_chat_messages, {
        store_id: storeId,
      });
      if (requestId !== latestRequest.current) return;
      setScheduledMessages(payloadOf(response).scheduled_messages || []);
    } catch (error) {
      console.error("Error loading scheduled messages:", error);
    }
  }, [storeId, userId]);

  // The list is the user's own messages across all their companies, so a
  // store switch only refetches it; there's nothing store-specific to clear.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // A scheduled message reaching Stream (ids start "sched-") means the list
  // changed; message.new covers watched channels, notification.message_new
  // the rest.
  useEffect(() => {
    if (!client || !userId) return undefined;
    const onMessage = (event) => {
      if (
        isScheduledStreamMessageId(event?.message?.id) &&
        String(event?.message?.user?.id ?? event?.user?.id) === String(userId)
      ) {
        refresh();
      }
    };
    const subscriptions = [
      client.on("message.new", onMessage),
      client.on("notification.message_new", onMessage),
    ];
    return () => subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
  }, [client, userId, refresh]);

  // A row past its time is waiting on the once-a-minute sender; recheck so a
  // send (or failure) shows up even if no Stream event reaches this client.
  const hasOverdue = scheduledMessages.some(
    (row) => row.status !== "failed" && new Date(row.send_at) <= new Date()
  );
  const nextSendAt = scheduledMessages
    .filter((row) => row.status !== "failed")
    .map((row) => new Date(row.send_at).getTime())
    .sort((a, b) => a - b)[0];
  useEffect(() => {
    if (!hasOverdue && !nextSendAt) return undefined;
    const delay = hasOverdue
      ? RECHECK_MS
      : Math.max(nextSendAt - Date.now() + RECHECK_MS, RECHECK_MS);
    // setTimeout caps at ~24.8 days
    const timer = setTimeout(refresh, Math.min(delay, 2 ** 31 - 1));
    return () => clearTimeout(timer);
  }, [hasOverdue, nextSendAt, refresh]);

  const schedule = useCallback(
    async ({ channelType, channelId, sendAt, message, clientMessageId }) => {
      const response = await postRightworkAPI(API_schedule_chat_message, {
        store_id: storeId,
        channel_type: channelType,
        channel_id: channelId,
        send_at: new Date(sendAt).toISOString(),
        client_message_id: clientMessageId,
        ...message,
      });
      const row = payloadOf(response).scheduled_message;
      if (row) {
        retireInFlightRefreshes();
        setScheduledMessages((previous) =>
          [...previous.filter((item) => item.id !== row.id), row].sort(
            (a, b) => new Date(a.send_at) - new Date(b.send_at)
          )
        );
      }
      return row;
    },
    [storeId]
  );

  const update = useCallback(
    async (id, { text, sendAt }) => {
      const body = { store_id: storeId, scheduled_message_id: id };
      if (text !== undefined) body.text = text;
      if (sendAt !== undefined) body.send_at = new Date(sendAt).toISOString();
      const response = await postRightworkAPI(API_update_scheduled_chat_message, body);
      const row = payloadOf(response).scheduled_message;
      if (row) {
        retireInFlightRefreshes();
        setScheduledMessages((previous) =>
          previous
            .map((item) => (item.id === row.id ? { ...item, ...row } : item))
            .sort((a, b) => new Date(a.send_at) - new Date(b.send_at))
        );
      }
      return row;
    },
    [storeId]
  );

  const cancel = useCallback(
    async (id) => {
      await postRightworkAPI(API_cancel_scheduled_chat_message, {
        store_id: storeId,
        scheduled_message_id: id,
      });
      retireInFlightRefreshes();
      setScheduledMessages((previous) => previous.filter((item) => item.id !== id));
    },
    [storeId]
  );

  const sendNow = useCallback(
    async (id) => {
      await postRightworkAPI(API_send_scheduled_chat_message_now, {
        store_id: storeId,
        scheduled_message_id: id,
      });
      // Gone from the list once sent. A send that fails comes back as a
      // failed row, so check again once the worker has had time to try.
      retireInFlightRefreshes();
      setScheduledMessages((previous) => previous.filter((item) => item.id !== id));
      setTimeout(refresh, 5000);
    },
    [storeId, refresh]
  );

  const countsByChannel = useMemo(
    () => countScheduledByChannel(scheduledMessages),
    [scheduledMessages]
  );

  return useMemo(
    () => ({
      scheduledMessages,
      countsByChannel,
      refresh,
      schedule,
      update,
      cancel,
      sendNow,
      openList: onOpenList,
      listOpen,
    }),
    [scheduledMessages, countsByChannel, refresh, schedule, update, cancel, sendNow, onOpenList, listOpen]
  );
}

export function ScheduledMessagesProvider({ value, children }) {
  return (
    <ScheduledMessagesContext.Provider value={value}>
      {children}
    </ScheduledMessagesContext.Provider>
  );
}
